import React from 'react';
import { getViridisGradientStops } from '../../../../../utils/renewableUtils';

// Card style for the floating-over-the-map legend (discrete percentile + calculating).
const OVERLAY_CARD_CLASSES =
  'absolute bottom-4 right-4 z-[1000] pointer-events-auto select-none bg-white border border-gray-200 rounded-lg p-3 shadow-lg max-w-xs';

// Card style for the inline legend (continuous clustered C.F bars) rendered next to the stats row.
const INLINE_CARD_CLASSES = 'select-none bg-white rounded-lg p-2';

/**
 * Onshore/Offshore wind legend.
 *
 * placement="overlay" → renders inside the map's relative container, absolute-positioned.
 *                       Used for the discrete (percentile) bullet list and the
 *                       "Calculating wind thresholds..." placeholder.
 * placement="inline"  → renders alongside the stats row.
 *                       Used only for the continuous (clustered) viridis bars.
 *
 * Each placement returns null when the current mode does not belong to it, so the
 * parent card can mount the component twice (once in each slot) without duplicates.
 */
const WindOnshoreOffshoreLegendPanel = ({
  onshoreWindData,
  offshoreWindData,
  onshoreWindThresholds,
  offshoreWindThresholds,
  showOnshoreLayer = true,
  showOffshoreLayer = true,
  placement = 'inline',
}) => {
  const hasVisibleOnshoreData = Boolean(showOnshoreLayer && onshoreWindData);
  const hasVisibleOffshoreData = Boolean(showOffshoreLayer && offshoreWindData);

  if (!hasVisibleOnshoreData && !hasVisibleOffshoreData) {
    return null;
  }

  const onshoreContinuous = showOnshoreLayer && onshoreWindThresholds?.mode === 'continuous';
  const offshoreContinuous = showOffshoreLayer && offshoreWindThresholds?.mode === 'continuous';
  const anyContinuous = onshoreContinuous || offshoreContinuous;

  if (placement === 'inline') {
    if (!anyContinuous) return null;

    return (
      <div className={INLINE_CARD_CLASSES}>
        <div className="flex flex-col gap-2">
          {hasVisibleOnshoreData && onshoreContinuous && (
            <div className="flex flex-col gap-0.5">
              <div className="text-[11px] font-semibold text-gray-800 leading-none whitespace-nowrap text-center">
                🌬️ C.F (windon)
              </div>
              <div className="flex flex-row items-center gap-1.5">
                <div className="text-[10px] text-gray-700 leading-none whitespace-nowrap">
                  {(onshoreWindThresholds.vmin * 100).toFixed(2)}%
                </div>
                <div
                  className="rounded-sm flex-1"
                  style={{
                    minWidth: 100,
                    height: 10,
                    background: `linear-gradient(to right, ${getViridisGradientStops(14)})`,
                    border: '1px solid rgba(0,0,0,0.12)'
                  }}
                />
                <div className="text-[10px] text-gray-700 leading-none whitespace-nowrap">
                  {(onshoreWindThresholds.vmax * 100).toFixed(2)}%
                </div>
              </div>
            </div>
          )}

          {hasVisibleOffshoreData && offshoreContinuous && (
            <div className="flex flex-col gap-0.5">
              <div className="text-[11px] font-semibold text-gray-800 leading-none whitespace-nowrap text-center">
                🌊 C.F (windoff)
              </div>
              <div className="flex flex-row items-center gap-1.5">
                <div className="text-[10px] text-gray-700 leading-none whitespace-nowrap">
                  {(offshoreWindThresholds.vmin * 100).toFixed(2)}%
                </div>
                <div
                  className="rounded-sm flex-1"
                  style={{
                    minWidth: 100,
                    height: 10,
                    background: `linear-gradient(to right, ${getViridisGradientStops(14)})`,
                    border: '1px solid rgba(0,0,0,0.12)'
                  }}
                />
                <div className="text-[10px] text-gray-700 leading-none whitespace-nowrap">
                  {(offshoreWindThresholds.vmax * 100).toFixed(2)}%
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // placement === 'overlay'
  // If everything we have is continuous, the inline placement handles it.
  const onshoreNeedsOverlay =
    hasVisibleOnshoreData && onshoreWindThresholds && !onshoreContinuous;
  const offshoreNeedsOverlay =
    hasVisibleOffshoreData && offshoreWindThresholds && !offshoreContinuous;
  const calculating =
    (hasVisibleOnshoreData && !onshoreWindThresholds) ||
    (hasVisibleOffshoreData && !offshoreWindThresholds);

  if (!onshoreNeedsOverlay && !offshoreNeedsOverlay && !calculating) {
    return null;
  }

  return (
    <div className={OVERLAY_CARD_CLASSES}>
      <div className="space-y-2">
        {onshoreNeedsOverlay && (
          <div>
            <div className="text-xs font-semibold text-gray-700 mb-1 border-b border-gray-200 pb-1">
              🌬️ Onshore Wind
            </div>
            <div className="space-y-1">
              <div className="flex items-center text-xs">
                <div
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: onshoreWindThresholds.excellent.color }}
                ></div>
                <span>
                  Excellent: {(onshoreWindThresholds.excellent.threshold * 100).toFixed(1)}%+
                </span>
              </div>
              <div className="flex items-center text-xs">
                <div
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: onshoreWindThresholds.high.color }}
                ></div>
                <span>
                  High: {(onshoreWindThresholds.high.threshold * 100).toFixed(1)}-
                  {(onshoreWindThresholds.excellent.threshold * 100).toFixed(1)}%
                </span>
              </div>
              <div className="flex items-center text-xs">
                <div
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: onshoreWindThresholds.good.color }}
                ></div>
                <span>
                  Good: {(onshoreWindThresholds.good.threshold * 100).toFixed(1)}-
                  {(onshoreWindThresholds.high.threshold * 100).toFixed(1)}%
                </span>
              </div>
              <div className="flex items-center text-xs">
                <div
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: onshoreWindThresholds.fair.color }}
                ></div>
                <span>
                  Fair: {(onshoreWindThresholds.fair.threshold * 100).toFixed(1)}-
                  {(onshoreWindThresholds.good.threshold * 100).toFixed(1)}%
                </span>
              </div>
              <div className="flex items-center text-xs">
                <div
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: onshoreWindThresholds.poor.color }}
                ></div>
                <span>
                  Poor: &lt;{(onshoreWindThresholds.fair.threshold * 100).toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
        )}

        {offshoreNeedsOverlay && (
          <div>
            <div className="text-xs font-semibold text-gray-700 mb-1 border-b border-gray-200 pb-1">
              🌊 Offshore Wind
            </div>
            <div className="space-y-1">
              <div className="flex items-center text-xs">
                <div
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: offshoreWindThresholds.excellent.color }}
                ></div>
                <span>
                  Excellent: {(offshoreWindThresholds.excellent.threshold * 100).toFixed(1)}%+
                </span>
              </div>
              <div className="flex items-center text-xs">
                <div
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: offshoreWindThresholds.high.color }}
                ></div>
                <span>
                  High: {(offshoreWindThresholds.high.threshold * 100).toFixed(1)}-
                  {(offshoreWindThresholds.excellent.threshold * 100).toFixed(1)}%
                </span>
              </div>
              <div className="flex items-center text-xs">
                <div
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: offshoreWindThresholds.good.color }}
                ></div>
                <span>
                  Good: {(offshoreWindThresholds.good.threshold * 100).toFixed(1)}-
                  {(offshoreWindThresholds.high.threshold * 100).toFixed(1)}%
                </span>
              </div>
              <div className="flex items-center text-xs">
                <div
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: offshoreWindThresholds.fair.color }}
                ></div>
                <span>
                  Fair: {(offshoreWindThresholds.fair.threshold * 100).toFixed(1)}-
                  {(offshoreWindThresholds.good.threshold * 100).toFixed(1)}%
                </span>
              </div>
              <div className="flex items-center text-xs">
                <div
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: offshoreWindThresholds.poor.color }}
                ></div>
                <span>
                  Poor: &lt;{(offshoreWindThresholds.fair.threshold * 100).toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
        )}

        {calculating && (
          <div className="text-xs text-gray-600">
            <span>Calculating wind thresholds...</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default WindOnshoreOffshoreLegendPanel;
